import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Loader, RefreshCw } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeesQueryKey,
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdLatestQueryKey,
  usePostApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdImport,
} from '@/api/generated/company-management-user/company-management-user'
import { PermissionEntityType } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import { Button } from '@/components/shadcn/button'
import { notify } from '@/lib/notify'

interface Props {
  serviceModuleId: number
}

/**
 * Triggers the server-side import of personal documents from the service
 * connection configured for this module. The import runs synchronously in the
 * request, so the button stays pending until the backend answers.
 */
export function ImportDocumentsButton({ serviceModuleId }: Props) {
  const { t } = useTranslation('service-modules-personal-documents')
  const queryClient = useQueryClient()
  const { userHasPermission } = usePermissions()
  // Same gate as the endpoint (`ServiceConnections.Edit`, global).
  const canImport = userHasPermission(PermissionEntityType.ServiceConnections, 'Edit')

  const { mutate: triggerImport, isPending } =
    usePostApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdImport({
      mutation: {
        // The import runs synchronously in the request and can take a few
        // seconds — acknowledge the click right away instead of leaving the
        // user with nothing but the spinner.
        onMutate: () =>
          notify.info(t('personalDocuments.importStarted'), {
            description: t('personalDocuments.importStartedHint'),
          }),
        onSuccess: () => {
          notify.success(t('personalDocuments.importSuccess'))
          queryClient.invalidateQueries({
            queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeesQueryKey(serviceModuleId),
          })
          queryClient.invalidateQueries({
            queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdLatestQueryKey(serviceModuleId),
          })
        },
        onError: (error) => notify.apiError(error, t('personalDocuments.importError')),
      },
    })

  return (
    <Button
      className="h-9 gap-2"
      disabled={!canImport || isPending}
      onClick={() => triggerImport({ serviceModuleId })}
    >
      {isPending
        ? <Loader className="size-4 animate-spin" />
        : <RefreshCw className="size-4" />}
      <span className="hidden sm:inline">{t('personalDocuments.import')}</span>
    </Button>
  )
}
